import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, descargar } from "../lib/api";
import {
  creditos,
  fecha,
  fechaHora,
  formatearCantidad,
  gramos,
  sufijoUnidad,
} from "../lib/formato";
import { useIdioma } from "../lib/idioma";
import type { Clave } from "../idiomas/es";
import {
  Aviso,
  Cabecera,
  Campo,
  Cargando,
  Etiqueta,
  Modal,
  Panel,
  TarjetaDato,
  ThOrden,
  Vacio,
} from "../componentes/piezas";
import { useSesion } from "../lib/sesion";

interface Producto {
  id: string;
  nombre: string;
  stockMg: number;
  stockMinMg: number;
  precioCentimos: number;
  activo: boolean;
  categoria: { nombre: string; color: string; unidadMedida: string };
}

interface Movimiento {
  id: string;
  fecha: string;
  tipo: string;
  cantidadMg: number;
  stockNuevo: number;
  motivo: string | null;
  producto: { nombre: string; categoria: { unidadMedida: string } };
  usuario: { nombre: string } | null;
  lote: { codigo: string } | null;
}

interface Alertas {
  stockBajo: {
    id: string;
    nombre: string;
    stockMg: number;
    stockMinMg: number;
  }[];
  lotesPorCaducar: {
    id: string;
    codigo: string;
    caducidad: string;
    cantidadActualMg: number;
    producto: { nombre: string };
  }[];
}

interface FilaEstado {
  id: string;
  nombre: string;
  activo: boolean;
  categoria: {
    id: string;
    nombre: string;
    color: string;
    unidadMedida: string;
  };
  stockMg: number;
  stockGuardadoMg: number;
  totalMg: number;
  stockMinMg: number;
  bajoMinimo: boolean;
  precioCentimos: number;
  valorCentimos: number;
  lotesAbiertos: number;
  costeCentimos: number;
  proximaCaducidad: string | null;
}

interface Estado {
  productos: FilaEstado[];
  totales: {
    productos: number;
    stockMg: number;
    guardadoMg: number;
    totalMg: number;
    valorCentimos: number;
    costeCentimos: number;
    bajoMinimo: number;
  };
}

type Operacion = {
  tipo: "entrada" | "merma" | "ajuste";
  producto: Producto;
} | null;
type Columna =
  "nombre" | "categoria" | "stock" | "guardado" | "valor" | "coste";

export default function Inventario() {
  const { t } = useIdioma();
  const { puede } = useSesion();
  const clienteConsultas = useQueryClient();
  const [operacion, setOperacion] = useState<Operacion>(null);
  const [error, setError] = useState("");
  const [vista, setVista] = useState<"almacen" | "movimientos">("almacen");
  const [orden, setOrden] = useState<Columna>("nombre");
  const [descendente, setDescendente] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [moviendo, setMoviendo] = useState<FilaEstado | null>(null);

  const productos = useQuery({
    queryKey: ["productos"],
    queryFn: () => api<Producto[]>("/catalogo/productos"),
  });
  const movimientos = useQuery({
    queryKey: ["movimientos-stock"],
    queryFn: () =>
      api<Movimiento[]>("/inventario/movimientos", { query: { limite: 60 } }),
  });
  const alertas = useQuery({
    queryKey: ["alertas-stock"],
    queryFn: () => api<Alertas>("/inventario/alertas"),
  });
  const estado = useQuery({
    queryKey: ["estado-inventario"],
    queryFn: () => api<Estado>("/inventario/estado"),
  });
  const valoracion = useQuery({
    queryKey: ["valoracion"],
    queryFn: () =>
      api<{ totalMg: number; totalCentimos: number }>("/inventario/valoracion"),
  });

  /** Ordena por la columna pedida; volver a pulsarla le da la vuelta. */
  const ordenarPor = (columna: Columna) => {
    if (columna === orden) setDescendente((d) => !d);
    else {
      setOrden(columna);
      setDescendente(columna !== "nombre" && columna !== "categoria");
    }
  };

  const texto = busqueda.trim().toLowerCase();
  const visibles = (estado.data?.productos ?? [])
    .filter(
      (p) =>
        !texto ||
        p.nombre.toLowerCase().includes(texto) ||
        p.categoria.nombre.toLowerCase().includes(texto),
    )
    .sort((a, b) => {
      const signo = descendente ? -1 : 1;
      switch (orden) {
        case "categoria":
          return signo * a.categoria.nombre.localeCompare(b.categoria.nombre);
        case "stock":
          return signo * (a.stockMg - b.stockMg);
        case "guardado":
          return signo * (a.stockGuardadoMg - b.stockGuardadoMg);
        case "valor":
          return signo * (a.valorCentimos - b.valorCentimos);
        case "coste":
          return signo * (a.costeCentimos - b.costeCentimos);
        default:
          return signo * a.nombre.localeCompare(b.nombre);
      }
    });

  const refrescar = () => {
    for (const clave of [
      "productos",
      "movimientos-stock",
      "alertas-stock",
      "valoracion",
      "estado-inventario",
      "productos-mostrador",
    ]) {
      void clienteConsultas.invalidateQueries({ queryKey: [clave] });
    }
  };

  return (
    <>
      <Cabecera titulo={t("i.titulo")} descripcion={t("i.descripcion")}>
        <button
          className={`boton ${vista === "almacen" ? "" : "secundario"}`}
          onClick={() => setVista("almacen")}
        >
          {t("i.almacen")}
        </button>
        <button
          className={`boton ${vista === "movimientos" ? "" : "secundario"}`}
          onClick={() => setVista("movimientos")}
        >
          {t("i.movimientos")}
        </button>
        <button
          className="boton secundario"
          onClick={() =>
            descargar("/exportar/inventario.csv", "inventario.csv").catch(
              (fallo) =>
                setError(
                  fallo instanceof Error ? fallo.message : t("i.falloExportar"),
                ),
            )
          }
        >
          {t("i.exportar")}
        </button>
      </Cabecera>

      <Aviso tipo="error">{error}</Aviso>

      <div className="rejilla cuatro" style={{ marginBottom: 16 }}>
        <TarjetaDato
          etiqueta={t("i.enElMostrador")}
          valor={estado.data ? gramos(estado.data.totales.stockMg, 0) : "—"}
          pie={
            estado.data
              ? t("i.guardadosPie", {
                  cantidad: gramos(estado.data.totales.guardadoMg, 0),
                })
              : undefined
          }
        />
        <TarjetaDato
          etiqueta={t("i.valor")}
          valor={valoracion.data ? creditos(valoracion.data.totalCentimos) : "—"}
          pie={t("i.valorPie")}
        />
        {/* Lo que vale y lo que costo no son lo mismo, y suelen preguntar los dos. */}
        <TarjetaDato
          etiqueta={t("i.coste")}
          valor={estado.data ? creditos(estado.data.totales.costeCentimos) : "—"}
          pie={t("i.costePie")}
        />
        <TarjetaDato
          etiqueta={t("i.avisos")}
          valor={
            (alertas.data?.stockBajo.length ?? 0) +
            (alertas.data?.lotesPorCaducar.length ?? 0)
          }
          pie={t("i.avisosPie", {
            bajos: alertas.data?.stockBajo.length ?? 0,
            caducan: alertas.data?.lotesPorCaducar.length ?? 0,
          })}
        />
      </div>

      {alertas.data && alertas.data.lotesPorCaducar.length > 0 && (
        <Aviso tipo="atencion">
          {t("i.porCaducar")}{" "}
          {alertas.data.lotesPorCaducar
            .map(
              (l) =>
                `${l.producto.nombre} (${l.codigo}, ${fecha(l.caducidad)})`,
            )
            .join(", ")}
        </Aviso>
      )}

      {vista === "almacen" ? (
        <Panel sinCuerpo>
          <div className="panel-cabecera">
            <input
              style={{ maxWidth: 320 }}
              placeholder={t("i.buscar")}
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
            <span className="suave pequeno">
              {estado.data
                ? estado.data.totales.productos === 1
                  ? t("i.unProducto")
                  : t("i.nProductos", { n: estado.data.totales.productos })
                : ""}
            </span>
          </div>

          {estado.isLoading ? (
            <Cargando />
          ) : !estado.data || estado.data.productos.length === 0 ? (
            <Vacio>{t("i.sinProductos")}</Vacio>
          ) : (
            <div className="tabla-envoltorio">
              <table className="tabla">
                <thead>
                  <tr>
                    <ThOrden
                      columna="nombre"
                      orden={orden}
                      descendente={descendente}
                      alOrdenar={ordenarPor}
                    >
                      {t("i.producto")}
                    </ThOrden>
                    <ThOrden
                      columna="categoria"
                      orden={orden}
                      descendente={descendente}
                      alOrdenar={ordenarPor}
                    >
                      {t("i.categoria")}
                    </ThOrden>
                    <th>{t("i.unidad")}</th>
                    <ThOrden
                      columna="stock"
                      orden={orden}
                      descendente={descendente}
                      alOrdenar={ordenarPor}
                      num
                    >
                      {t("i.enVenta")}
                    </ThOrden>
                    <ThOrden
                      columna="guardado"
                      orden={orden}
                      descendente={descendente}
                      alOrdenar={ordenarPor}
                      num
                    >
                      {t("i.guardado")}
                    </ThOrden>
                    <th className="num">{t("i.total")}</th>
                    <th className="num">{t("i.minimo")}</th>
                    <th className="num">{t("i.lotes")}</th>
                    <th className="num">{t("i.caduca")}</th>
                    <ThOrden
                      columna="valor"
                      orden={orden}
                      descendente={descendente}
                      alOrdenar={ordenarPor}
                      num
                    >
                      {t("i.valorColumna")}
                    </ThOrden>
                    <ThOrden
                      columna="coste"
                      orden={orden}
                      descendente={descendente}
                      alOrdenar={ordenarPor}
                      num
                    >
                      {t("i.costeColumna")}
                    </ThOrden>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {visibles.map((p) => (
                    <tr
                      key={p.id}
                      style={p.activo ? undefined : { opacity: 0.55 }}
                    >
                      <td>
                        <Link to={`/catalogo/${p.id}`}>
                          <strong>{p.nombre}</strong>
                        </Link>
                        {!p.activo && (
                          <div className="suave pequeno">{t("i.inactivo")}</div>
                        )}
                      </td>
                      <td>
                        <span
                          className="etiqueta"
                          style={{
                            background: `${p.categoria.color}20`,
                            color: p.categoria.color,
                          }}
                        >
                          {p.categoria.nombre}
                        </span>
                      </td>
                      <td className="pequeno suave">
                        {sufijoUnidad(p.categoria.unidadMedida)}
                      </td>
                      <td className="num tabular">
                        {formatearCantidad(p.stockMg, p.categoria.unidadMedida)}
                        {p.bajoMinimo && (
                          <div>
                            <Etiqueta tipo="roja">{t("i.bajoMinimo")}</Etiqueta>
                          </div>
                        )}
                      </td>
                      <td className="num tabular">
                        {p.stockGuardadoMg > 0 ? (
                          <strong>
                            {formatearCantidad(
                              p.stockGuardadoMg,
                              p.categoria.unidadMedida,
                            )}
                          </strong>
                        ) : (
                          <span className="suave">—</span>
                        )}
                      </td>
                      <td className="num tabular suave">
                        {formatearCantidad(p.totalMg, p.categoria.unidadMedida)}
                      </td>
                      <td className="num tabular suave pequeno">
                        {p.stockMinMg > 0
                          ? formatearCantidad(
                              p.stockMinMg,
                              p.categoria.unidadMedida,
                            )
                          : "—"}
                      </td>
                      <td className="num tabular suave">
                        {p.lotesAbiertos || "—"}
                      </td>
                      <td className="num pequeno suave">
                        {p.proximaCaducidad ? fecha(p.proximaCaducidad) : "—"}
                      </td>
                      <td className="num tabular">{creditos(p.valorCentimos)}</td>
                      <td className="num tabular suave">
                        {creditos(p.costeCentimos)}
                      </td>
                      <td>
                        <div
                          className="fila"
                          style={{ gap: 2, justifyContent: "flex-end" }}
                        >
                          {puede("moverGuardado") && (
                            <button
                              className="boton fantasma"
                              onClick={() => setMoviendo(p)}
                            >
                              {t("i.guardado")}
                            </button>
                          )}
                          {(["entrada", "merma", "ajuste"] as const).map(
                            (tipo) => (
                              <button
                                key={tipo}
                                className="boton fantasma"
                                onClick={() =>
                                  setOperacion({
                                    tipo,
                                    producto: {
                                      id: p.id,
                                      nombre: p.nombre,
                                      stockMg: p.stockMg,
                                      stockMinMg: p.stockMinMg,
                                      precioCentimos: p.precioCentimos,
                                      activo: p.activo,
                                      categoria: p.categoria,
                                    },
                                  })
                                }
                              >
                                {t(`i.${tipo}` as Clave)}
                              </button>
                            ),
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      ) : (
        <Panel titulo={t("i.ultimos")} sinCuerpo>
          {movimientos.isLoading ? (
            <Cargando />
          ) : !movimientos.data || movimientos.data.length === 0 ? (
            <Vacio>{t("i.sinRegistros")}</Vacio>
          ) : (
            <div className="tabla-envoltorio">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>{t("i.fecha")}</th>
                    <th>{t("i.producto")}</th>
                    <th>{t("i.tipo")}</th>
                    <th className="num">{t("i.cantidad")}</th>
                  </tr>
                </thead>
                <tbody>
                  {movimientos.data.map((m) => (
                    <tr key={m.id}>
                      <td className="pequeno suave">{fechaHora(m.fecha)}</td>
                      <td className="pequeno">
                        {m.producto.nombre}
                        {m.motivo && <div className="suave">{m.motivo}</div>}
                      </td>
                      <td>
                        <Etiqueta
                          tipo={
                            m.cantidadMg >= 0
                              ? "verde"
                              : m.tipo === "MERMA"
                                ? "roja"
                                : ""
                          }
                        >
                          {t(`movimiento.${m.tipo}` as Clave)}
                        </Etiqueta>
                      </td>
                      <td
                        className="num tabular"
                        style={{
                          color:
                            m.cantidadMg >= 0 ? "var(--verde)" : "var(--rojo)",
                        }}
                      >
                        {m.cantidadMg >= 0 ? "+" : "−"}
                        {formatearCantidad(
                          Math.abs(m.cantidadMg),
                          m.producto.categoria.unidadMedida,
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}

      {moviendo && (
        <DialogoGuardado
          producto={moviendo}
          alCerrar={() => setMoviendo(null)}
          alFallar={setError}
          alHecho={() => {
            setMoviendo(null);
            setError("");
            refrescar();
          }}
        />
      )}

      {operacion && (
        <DialogoOperacion
          operacion={operacion}
          alCerrar={() => setOperacion(null)}
          alFallar={setError}
          alHecho={() => {
            setOperacion(null);
            refrescar();
          }}
        />
      )}
    </>
  );
}

function DialogoOperacion({
  operacion,
  alCerrar,
  alHecho,
  alFallar,
}: {
  operacion: NonNullable<Operacion>;
  alCerrar: () => void;
  alHecho: () => void;
  alFallar: (mensaje: string) => void;
}) {
  const { tipo, producto } = operacion;
  const { t } = useIdioma();
  const [cantidad, setCantidad] = useState(
    tipo === "ajuste" ? String(producto.stockMg / 1000) : "",
  );
  const [motivo, setMotivo] = useState("");
  const [coste, setCoste] = useState("");
  const [proveedor, setProveedor] = useState("");
  const [codigo, setCodigo] = useState("");
  const [caducidad, setCaducidad] = useState("");
  const [enviando, setEnviando] = useState(false);

  const titulos = {
    entrada: t("i.op.entrada", { producto: producto.nombre }),
    merma: t("i.op.merma", { producto: producto.nombre }),
    ajuste: t("i.op.ajuste", { producto: producto.nombre }),
  };

  async function guardar() {
    setEnviando(true);
    try {
      if (tipo === "entrada") {
        await api("/inventario/entradas", {
          metodo: "POST",
          cuerpo: {
            productoId: producto.id,
            cantidadG: Number(cantidad),
            costeEuros: Number(coste || 0),
            proveedor: proveedor || null,
            codigo: codigo || undefined,
            caducidad: caducidad || null,
          },
        });
      } else if (tipo === "merma") {
        await api("/inventario/mermas", {
          metodo: "POST",
          cuerpo: {
            productoId: producto.id,
            cantidadG: Number(cantidad),
            motivo,
          },
        });
      } else {
        await api("/inventario/ajustes", {
          metodo: "POST",
          cuerpo: {
            productoId: producto.id,
            stockRealG: Number(cantidad),
            motivo,
          },
        });
      }
      alHecho();
    } catch (fallo) {
      alFallar(fallo instanceof Error ? fallo.message : t("i.op.fallo"));
      alCerrar();
    } finally {
      setEnviando(false);
    }
  }

  const valido =
    Number(cantidad) >= 0 &&
    cantidad !== "" &&
    (tipo === "entrada" ? Number(cantidad) > 0 : motivo.trim().length >= 3);

  return (
    <Modal
      titulo={titulos[tipo]}
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            {t("g.cancelar")}
          </button>
          <button
            className="boton"
            onClick={guardar}
            disabled={enviando || !valido}
          >
            {enviando ? t("g.guardando") : t("i.op.registrar")}
          </button>
        </>
      }
    >
      <p className="suave pequeno" style={{ marginTop: 0 }}>
        {t("i.op.stockActual")}{" "}
        <strong>
          {formatearCantidad(producto.stockMg, producto.categoria.unidadMedida)}
        </strong>
      </p>

      <Campo
        etiqueta={`${tipo === "ajuste" ? t("i.op.stockContado") : t("i.op.cantidad")} (${sufijoUnidad(producto.categoria.unidadMedida)})`}
        ayuda={tipo === "ajuste" ? t("i.op.ajusteAyuda") : undefined}
      >
        <input
          type="number"
          step="0.1"
          min={0}
          value={cantidad}
          onChange={(e) => setCantidad(e.target.value)}
          autoFocus
        />
      </Campo>

      {tipo === "entrada" ? (
        <>
          <div className="linea-campos">
            <Campo
              etiqueta={t("i.op.codigoLote")}
              ayuda={t("i.op.codigoAyuda")}
            >
              <input
                value={codigo}
                onChange={(e) => setCodigo(e.target.value)}
              />
            </Campo>
            <Campo etiqueta={t("i.op.costeTotal")}>
              <input
                type="number"
                step="0.01"
                min={0}
                value={coste}
                onChange={(e) => setCoste(e.target.value)}
              />
            </Campo>
          </div>
          <div className="linea-campos">
            <Campo etiqueta={t("i.op.proveedor")}>
              <input
                value={proveedor}
                onChange={(e) => setProveedor(e.target.value)}
              />
            </Campo>
            <Campo etiqueta={t("i.op.caducidad")}>
              <input
                type="date"
                value={caducidad}
                onChange={(e) => setCaducidad(e.target.value)}
              />
            </Campo>
          </div>
        </>
      ) : (
        <Campo etiqueta={t("i.op.motivo")} ayuda={t("i.op.motivoAyuda")}>
          <textarea
            rows={2}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
          />
        </Campo>
      )}
    </Modal>
  );
}

/**
 * Sacar género del guardado al mostrador, o devolverlo.
 *
 * El club compra cien gramos, pone veinte a la venta y guarda ochenta: así lo
 * accesible en el local es lo justo. Este diálogo es el único sitio donde se
 * cambia ese reparto, y solo lo ve quien puede hacerlo.
 */
function DialogoGuardado({
  producto,
  alCerrar,
  alHecho,
  alFallar,
}: {
  producto: FilaEstado;
  alCerrar: () => void;
  alHecho: () => void;
  alFallar: (m: string) => void;
}) {
  const { t } = useIdioma();
  const [hacia, setHacia] = useState<"VENTA" | "GUARDADO">("VENTA");
  const [cantidadG, setCantidadG] = useState("");
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);

  const disponible =
    hacia === "VENTA" ? producto.stockGuardadoMg : producto.stockMg;
  const pedido = Math.round((Number(cantidadG.replace(",", ".")) || 0) * 1000);
  const valido = pedido > 0 && pedido <= disponible;

  return (
    <Modal
      titulo={t("i.gd.titulo", { producto: producto.nombre })}
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            {t("g.cancelar")}
          </button>
          <button
            className="boton"
            disabled={enviando || !valido}
            onClick={async () => {
              setEnviando(true);
              try {
                await api("/inventario/mover-guardado", {
                  metodo: "POST",
                  cuerpo: {
                    productoId: producto.id,
                    cantidadG: Number(cantidadG.replace(",", ".")),
                    hacia,
                    motivo: motivo.trim() || undefined,
                  },
                });
                alHecho();
              } catch (fallo) {
                alFallar(
                  fallo instanceof Error ? fallo.message : t("i.gd.fallo"),
                );
                setEnviando(false);
              }
            }}
          >
            {enviando
              ? t("i.gd.moviendo")
              : hacia === "VENTA"
                ? t("i.gd.sacar")
                : t("g.guardar")}
          </button>
        </>
      }
    >
      <div className="fila separada" style={{ marginBottom: 12 }}>
        <span>
          {t("i.gd.enVenta")}{" "}
          <strong>
            {formatearCantidad(
              producto.stockMg,
              producto.categoria.unidadMedida,
            )}
          </strong>
        </span>
        <span>
          {t("i.gd.guardado")}{" "}
          <strong>
            {formatearCantidad(
              producto.stockGuardadoMg,
              producto.categoria.unidadMedida,
            )}
          </strong>
        </span>
      </div>

      <Campo etiqueta={t("i.gd.queSeHace")}>
        <select
          value={hacia}
          onChange={(e) => setHacia(e.target.value as "VENTA" | "GUARDADO")}
        >
          <option value="VENTA">{t("i.gd.aVenta")}</option>
          <option value="GUARDADO">{t("i.gd.aGuardado")}</option>
        </select>
      </Campo>

      <Campo
        etiqueta={t("i.op.cantidad")}
        ayuda={t("i.gd.comoMucho", {
          cantidad: formatearCantidad(
            disponible,
            producto.categoria.unidadMedida,
          ),
        })}
      >
        <input
          type="number"
          step="0.01"
          min={0}
          value={cantidadG}
          onChange={(e) => setCantidadG(e.target.value)}
          autoFocus
        />
      </Campo>

      <Campo etiqueta={t("i.op.motivo")} ayuda={t("i.gd.motivoAyuda")}>
        <input
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          maxLength={300}
        />
      </Campo>

      <p className="suave pequeno" style={{ marginBottom: 0 }}>
        El total del club no cambia: solo cambia de qué lado está. Lo guardado
        no se puede dispensar hasta que se saque.
      </p>
    </Modal>
  );
}
